// File: src/pages/EditRawProduct.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";

// ===== APIs & helpers (match your app layer paths) =====
import {
  AllCategoryList,
  _Get,
  _GetByUrl,
  _Put,
  _GetDataBydatabaseName,
} from "../import/ApiEndPoint/ApiCalling";

import {
  Create_Warehouse_List,
  PurchaseProductList_Product,
  viewOneRawProduct,
  updateRawProduct,
  viewUnits, // dynamic units like on the Create page
  viewProductionProductSection, // 🔹 sections list for dropdown
} from "../import/ApiEndPoint/Api";

// ===== tiny CSS injection (page scoped) =====
const injectOnce = () => {
  if (document.getElementById("erp-inline-css")) return;
  const style = document.createElement("style");
  style.id = "erp-inline-css";
  style.innerHTML = `
    .erp-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .erp-title{ font-weight:800; text-transform:uppercase; font-size:18px; letter-spacing:.02em; }
    .erp-help{ color:#64748b; font-size:12px; }
    .erp-box{ border:1px solid #e5e7eb; border-radius:14px; padding:12px; background:#fff; }
    .erp-chip{ display:inline-flex; gap:6px; align-items:center; padding:3px 8px; border-radius:999px; background:#e8f0ff; border:1px solid #c7d2fe; cursor:default }
    .erp-total{ border:1px solid #e5e7eb; border-radius:12px; padding:10px; }
  `;
  document.head.appendChild(style);
};

// ===== helpers =====
const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const money = (n) => (Number.isFinite(+n) ? Number(n).toFixed(2) : "0.00");

export default function EditRawProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  useEffect(() => injectOnce(), []);

  // UI
  const [loading, setLoading] = useState(true);

  // Lists
  const [wareHouseList, setWareHouseList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subcatlist, setSubcatlist] = useState([]);
  const [subStepslist, setSubStepslist] = useState([]);
  const [units, setUnits] = useState([]);
  const [purchaseProductList, setPurchaseProductList] = useState([]);
  const [rawProducts, setRawProducts] = useState([]);

  // 🔹 Production Product Sections for dropdown
  const [prodSections, setProdSections] = useState([]); // [{_id,name,description,status}]

  // State
  const [errors, setErrors] = useState({});
  const [isLastStep, setIsLastStep] = useState(false);

  const [formValues, setFormValues] = useState([{ unit: "", qty: "" }]); // product's unit rows
  const [Data, setData] = useState({
    id: "",
    database: "",
    productSection: "", // 🔹 match your model key
    warehouseId: "",
    warehouseName: "",
    category: "",
    SubCategory: "",
    SubSteps: "",
    NextSubStep: "",
    Product_Title: "",
    Opening_Stock: "",
    MIN_stockalert: "",
    status: "Active",
    stockUnit: "",
    secondaryUnit: "",
    rawValues: [
      {
        rawMaterial: "",
        rawProductId: "",
        unit: "",
        qty: "",
        rate: "",
        Price: "",
      },
    ],
    targetValues: [{ step: "", rawMaterial: "", rawProductId: "", unit: [] }],
    expensePercent: 17,
  });

  // ===== Derived =====
  const rawTotal = useMemo(
    () =>
      Data.rawValues.reduce(
        (acc, r) => acc + (r.Price === "" ? 0 : Number(r.Price || 0)),
        0
      ),
    [Data.rawValues]
  );
  const unitName = (id) => units.find((u) => u?.unitId === id)?.unitName || id;

  // ===== Hydrate (load all) =====
  const hydrate = async () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    if (!user?.database) {
      setLoading(false);
      return swal("Unable to determine your database.");
    }
    try {
      setLoading(true);

      // 1) Load the product to edit
      const productRes = await _GetByUrl(viewOneRawProduct, id);

      // 2) Parallel fetches for lists
      const [whRes, catRes, unitRes, purchRes, prodRes, secRes] =
        await Promise.all([
          _Get(Create_Warehouse_List, user.database),
          AllCategoryList(user._id, user.database),
          _GetByUrl(viewUnits, user.database),
          _Get(PurchaseProductList_Product, user.database),
          _GetDataBydatabaseName(user.database),
          _GetByUrl(viewProductionProductSection, user.database), // 🔹 sections
        ]);

      setWareHouseList(Array.isArray(whRes?.Warehouse) ? whRes.Warehouse : []);
      setCategoryList(Array.isArray(catRes?.Category) ? catRes.Category : []);
      setUnits(unitRes?.dynamicUnit?.Units || []);
      setPurchaseProductList(
        Array.isArray(purchRes?.Product) ? purchRes.Product : []
      );
      setRawProducts(Array.isArray(prodRes?.Product) ? prodRes.Product : []);

      const sections = Array.isArray(secRes?.ProductionProductSection)
        ? secRes.ProductionProductSection
        : [];
      setProdSections(sections);

      // Pre-fill product
      if (productRes?.Product) {
        const p = productRes.Product;

        // Subcats & steps for the chosen category
        const cat = (catRes?.Category || []).find(
          (c) => c?.name === p?.category
        );
        const subcats = Array.isArray(cat?.subcategories)
          ? cat.subcategories
          : [];
        const steps = Array.isArray(cat?.steps) ? cat.steps : [];

        setSubcatlist(subcats);
        setSubStepslist(steps);

        // Is last step?
        const idx = steps.findIndex((s) => s?.step_Name === p?.NextSubStep);
        setIsLastStep(idx >= 0 && idx + 1 === steps.length);

        // productSection from API may be:
        // - string (ObjectId or "RAW_MATERIAL")
        // - object ({ _id, name })
        const productSectionValue =
          typeof p?.productSection === "string"
            ? p.productSection
            : p?.productSection?._id || "";

        setData((prev) => ({
          ...prev,
          id: p?.id || p?.Product_Title || "",
          database: user.database,
          productSection: productSectionValue || "",
          warehouseId:
            typeof p?.warehouse === "string"
              ? p?.warehouse
              : p?.warehouse?._id || "",
          warehouseName: p?.warehouseName || "",
          category: p?.category || "",
          SubCategory: p?.SubCategory || "",
          SubSteps: p?.SubStep || p?.SubSteps || "",
          NextSubStep: p?.NextSubStep || "",
          Product_Title: p?.Product_Title || "",
          Opening_Stock: p?.Opening_Stock ?? "",
          MIN_stockalert: p?.MIN_stockalert ?? "",
          status: p?.status || "Active",
          stockUnit: p?.stockUnit || "",
          secondaryUnit: p?.secondaryUnit || "",
          expensePercent: p?.expensePercentage ?? 17,
          rawValues:
            Array.isArray(p?.rawValues) && p.rawValues.length
              ? p.rawValues.map((r) => ({
                  rawMaterial: r.rawMaterial || "",
                  rawProductId: r.rawProductId || "",
                  unit: r.unit || "",
                  qty: r.qty ?? "",
                  rate: r.rate ?? "",
                  Price: r.Price ?? "",
                }))
              : [
                  {
                    rawMaterial: "",
                    rawProductId: "",
                    unit: "",
                    qty: "",
                    rate: "",
                    Price: "",
                  },
                ],
          targetValues:
            Array.isArray(p?.targetValues) && p.targetValues.length
              ? p.targetValues.map((t) => ({
                  step: t.step || "",
                  rawMaterial: t.rawMaterial || "",
                  rawProductId: t.rawProductId || "",
                  unit: Array.isArray(t.unit) ? t.unit : [],
                }))
              : [{ step: "", rawMaterial: "", rawProductId: "", unit: [] }],
        }));

        setFormValues(
          Array.isArray(p?.Units) && p.Units.length
            ? p.Units
            : [{ unit: "", qty: "" }]
        );
      }

      setLoading(false);
    } catch (e) {
      console.error("[EditRawProduct] hydrate error:", e);
      setLoading(false);
      swal("Failed to load product or supporting data.");
    }
  };

  useEffect(() => {
    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ===== field handlers (mirror Create page) =====
  const onField = (e) => {
    const { name, value } = e.target;

    if (name === "warehouseId") {
      const wh = wareHouseList.find((w) => String(w?._id) === String(value));
      setData((p) => ({
        ...p,
        warehouseId: value,
        warehouseName: wh?.warehouseName || "",
      }));
      return;
    }

    if (name === "Product_Title") {
      const upper = String(value).toUpperCase();
      setData((p) => ({ ...p, Product_Title: upper }));
      return;
    }

    if (name === "category") {
      const cat = categoryList.find((c) => c?.name === value);
      const subcats = Array.isArray(cat?.subcategories)
        ? cat.subcategories
        : [];
      const steps = Array.isArray(cat?.steps) ? cat.steps : [];
      setSubcatlist(subcats);
      setSubStepslist(steps);
      setIsLastStep(false);
      setData((p) => ({
        ...p,
        category: value,
        SubCategory: "",
        SubSteps: "",
        NextSubStep: "",
      }));
      return;
    }

    if (name === "NextSubStep") {
      const idx = subStepslist.findIndex((s) => s?.step_Name === value);
      setIsLastStep(idx >= 0 && idx + 1 === subStepslist.length);
      setData((p) => ({ ...p, NextSubStep: value }));
      return;
    }

    setData((p) => ({ ...p, [name]: value }));
  };

  // Units rows
  const handleUnitRow = (i, e) => {
    const { name, value } = e.target;
    const list = formValues.slice();
    list[i][name] = name === "qty" ? toNum(value, "") : value;
    setFormValues(list);
  };
  const addUnitRow = () => setFormValues((p) => [...p, { unit: "", qty: "" }]);
  const removeUnitRow = (i) => {
    const list = formValues.slice();
    list.splice(i, 1);
    setFormValues(list);
  };

  // Costing rows (only on last step)
  const addRawRow = () =>
    setData((p) => ({
      ...p,
      rawValues: [
        ...p.rawValues,
        {
          rawMaterial: "",
          rawProductId: "",
          unit: "",
          qty: "",
          rate: "",
          Price: "",
        },
      ],
    }));
  const removeRawRow = (i) =>
    setData((p) => {
      const list = p.rawValues.slice();
      list.splice(i, 1);
      return { ...p, rawValues: list };
    });

  const onRawMaterial = (i, e) => {
    const { value } = e.target;
    const list = Data.rawValues.slice();
    const selected = purchaseProductList.find(
      (p) => p?.Product_Title === value
    );

    if (selected) {
      const rateBase = Number(selected?.Purchase_Rate || 0);
      const secSize = Number(selected?.secondarySize || 1);
      const ratePerUnit = secSize ? rateBase / secSize : rateBase;

      list[i] = {
        ...list[i],
        rawMaterial: value,
        rawProductId: selected?._id,
        unit: selected?.secondaryUnit || selected?.stockUnit || "",
        qty: 1,
        rate: Number(ratePerUnit).toFixed(4),
        Price: Number(ratePerUnit * 1).toFixed(6),
      };
    } else {
      list[i] = { ...list[i], rawMaterial: value };
    }
    setData((p) => ({ ...p, rawValues: list }));
  };

  const onRawQty = (i, e) => {
    const qty = toNum(e.target.value, 0);
    const list = Data.rawValues.slice();
    const row = list[i];
    if (!row) return;

    const base = purchaseProductList.find(
      (p) => p?.Product_Title === row.rawMaterial
    );
    if (base) {
      const baseRate = Number(base?.Purchase_Rate || 0);
      const secSize = Number(base?.secondarySize || 1);
      const perUnit = secSize ? baseRate / secSize : baseRate;
      const total = qty * perUnit;
      list[i] = { ...row, qty, Price: Number(total).toFixed(8) };
      setData((p) => ({ ...p, rawValues: list }));
    } else {
      list[i] = { ...row, qty, Price: "0.0000" };
      setData((p) => ({ ...p, rawValues: list }));
    }
  };

  // Targets rows (only on last step)
  const addTargetRow = () =>
    setData((p) => ({
      ...p,
      targetValues: [
        ...p.targetValues,
        { step: "", rawMaterial: "", rawProductId: "", unit: [] },
      ],
    }));
  const removeTargetRow = (i) =>
    setData((p) => {
      const list = p.targetValues.slice();
      list.splice(i, 1);
      return { ...p, targetValues: list };
    });

  const onTargetStep = (i, e) => {
    const { value } = e.target;
    const list = Data.targetValues.slice();
    list[i] = { ...list[i], step: value };
    setData((p) => ({ ...p, targetValues: list }));
  };

  const onTargetMaterial = (i, e) => {
    const { value } = e.target;
    const list = Data.targetValues.slice();
    const selected = rawProducts.find((p) => p?.Product_Title === value);
    list[i] = {
      ...list[i],
      rawMaterial: value,
      rawProductId: selected?._id || "",
      step: selected?.NextSubStep || list[i].step || "",
      unit: Array.isArray(selected?.Units) ? [...selected.Units] : [],
    };
    setData((p) => ({ ...p, targetValues: list }));
  };

  const onTargetUnitQty = (rowIndex, unitIndex, e) => {
    const qty = toNum(e.target.value, 0);
    const list = Data.targetValues.slice();
    const row = list[rowIndex];
    if (!row || !Array.isArray(row.unit)) return;

    // init conversion
    if (!row.unit.some((u) => u.__convInit)) {
      row.unit.forEach((u) => {
        u.conversionFactor = row.unit.map((v) => ({
          unit: v.unit,
          factor: u.qty && v.qty ? Number(v.qty) / Number(u.qty) : 0,
        }));
        u.__convInit = true;
      });
    }

    row.unit[unitIndex].qty = qty;
    row.unit[unitIndex].value = qty;

    const factors = row.unit[unitIndex].conversionFactor || [];
    row.unit.forEach((u, idx) => {
      if (idx === unitIndex) return;
      const f = factors.find((x) => x.unit === u.unit);
      if (f) {
        u.qty = Number(qty * f.factor).toFixed(4);
        u.value = Number(qty * f.factor).toFixed(4);
      }
    });

    list[rowIndex] = { ...row };
    setData((p) => ({ ...p, targetValues: list }));
  };

  // ===== Validation =====
  const validate = () => {
    const errs = {};
    if (!Data.warehouseId) errs.warehouseId = "Please select a Warehouse";

    const stockUnitChosen = formValues.some((u) => u.unit === Data.stockUnit);
    if (!stockUnitChosen)
      errs.stockUnit = "Unit mismatch with selected Stock Unit";

    // Keep the same policy: must include at least one Kg or SqIn
    const containsKgOrSqIn = formValues.some((u) => {
      const nm = unitName(u.unit);
      return nm === "Kg" || nm === "SqIn";
    });
    if (!containsKgOrSqIn)
      errs.unitPolicy = "Units must include at least one 'Kg' or 'SqIn'";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ===== Submit (PUT update) =====
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      database: Data.database,
      productSection: Data.productSection || "", // 🔹 include
      Units: formValues,
      category: Data.category,
      SubCategory: Data.SubCategory,
      SubStep: Data.SubSteps, // API expects SubStep
      NextSubStep: Data.NextSubStep,
      warehouse: Data.warehouseId,
      warehouseName: Data.warehouseName,
      stockUnit: Data.stockUnit,
      secondaryUnit: Data.secondaryUnit,
      Product_Title: Data.Product_Title,
      id: Data.id || Data.Product_Title,
      Opening_Stock: toNum(Data.Opening_Stock),
      MIN_stockalert: toNum(Data.MIN_stockalert),
      status: Data.status,

      // costing/targets only if present
      rawValues: Array.isArray(Data.rawValues) ? Data.rawValues : [],
      targetValues: Array.isArray(Data.targetValues) ? Data.targetValues : [],
      grossTotal: Number(rawTotal.toFixed(2)),
      expensePercentage: Number(Data.expensePercent),
      netAmount: Number(
        (rawTotal * (1 + toNum(Data.expensePercent) / 100)).toFixed(2)
      ),
    };

    try {
      setLoading(true);
      const res = await _Put(updateRawProduct, id, payload);
      setLoading(false);
      if (res?.status) {
        swal("Product details updated successfully");
        navigate(-1);
      } else {
        swal(res?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      swal("Error", "Something went wrong", "error");
    }
  };

  if (loading && !Data?.Product_Title) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  // Build Product Section dropdown options:
  // 1) fixed "RAW MATERIAL"
  // 2) sections from API
  const sectionOptions = [
    { _id: "RAW_MATERIAL", name: "PRODUCTION RAW PRODUCT" },
    ...prodSections,
  ];

  return (
    <Card className="erp-card">
      <CardBody>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div className="erp-title">Edit Raw Product</div>
          <div className="erp-chip">ID: {Data.id || "-"}</div>
          <div style={{ flex: 1 }} />
          <Button size="sm" color="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>

        <Form onSubmit={onSubmit}>
          {/* Top grid */}
          <Row className="erp-box" style={{ marginBottom: 12 }}>
            {/* 🔹 Product Section dropdown with RAW MATERIAL + API items */}
            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>Product Section</Label>
              <Input
                type="select"
                name="productSection"
                value={Data.productSection}
                onChange={onField}
              >
                <option value="">-- Select Section --</option>
                {sectionOptions.map((s) => (
                  <option key={s?._id} value={s?._id}>
                    {s?.name}
                  </option>
                ))}
              </Input>
              <div className="erp-help">
                Includes fixed “RAW MATERIAL” + API sections
              </div>
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Warehouse <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="select"
                name="warehouseId"
                value={Data.warehouseId}
                onChange={onField}
                style={{
                  border: errors.warehouseId ? "1px solid red" : undefined,
                }}
                required
              >
                <option value="">-- Select Warehouse --</option>
                {wareHouseList.map((w) => (
                  <option key={w?._id} value={w?._id}>
                    {w?.warehouseName}
                  </option>
                ))}
              </Input>
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Category <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="select"
                name="category"
                value={Data.category}
                onChange={onField}
                required
              >
                <option value="">-- Select Category --</option>
                {categoryList.map((c) => (
                  <option key={c?._id} value={c?.name}>
                    {c?.name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                SubCategory <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="select"
                name="SubCategory"
                value={Data.SubCategory}
                onChange={onField}
                required
              >
                <option value="">-- Select SubCategory --</option>
                {subcatlist.map((s) => (
                  <option key={s?._id} value={s?.name}>
                    {s?.name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Step <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="select"
                name="SubSteps"
                value={Data.SubSteps}
                onChange={onField}
                required
              >
                <option value="">-- Select Step --</option>
                {subStepslist.map((s) => (
                  <option key={s?._id} value={s?.step_Name}>
                    {s?.step_Name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Raw Step <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="select"
                name="NextSubStep"
                value={Data.NextSubStep}
                onChange={onField}
                required
              >
                <option value="">-- Select Raw Step --</option>
                {subStepslist.map((s) => (
                  <option key={s?._id} value={s?.step_Name}>
                    {s?.step_Name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Product Name <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                name="Product_Title"
                value={Data.Product_Title}
                onChange={onField}
                placeholder="Enter product name"
                required
              />
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Product Min Stock <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="number"
                name="MIN_stockalert"
                value={Data.MIN_stockalert}
                onChange={onField}
                placeholder="Min stock alert"
                required
              />
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Stock Unit <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="select"
                name="stockUnit"
                value={Data.stockUnit}
                onChange={onField}
                style={{
                  border: errors.stockUnit ? "1px solid red" : undefined,
                }}
                required
              >
                <option value="">-- Select --</option>
                {units.map((u) => (
                  <option key={u?.unitId} value={u?.unitId}>
                    {u?.unitName}
                  </option>
                ))}
              </Input>
              {errors.stockUnit && (
                <div className="erp-help" style={{ color: "red" }}>
                  {errors.stockUnit}
                </div>
              )}
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Secondary Unit <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                type="select"
                name="secondaryUnit"
                value={Data.secondaryUnit}
                onChange={onField}
                required
              >
                <option value="">-- Select --</option>
                {units.map((u) => (
                  <option key={u?.unitId} value={u?.unitId}>
                    {u?.unitName}
                  </option>
                ))}
              </Input>
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label>
                Opening Stock <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                name="Opening_Stock"
                value={Data.Opening_Stock}
                onChange={onField}
                placeholder="Opening stock"
                required
              />
            </Col>

            <Col lg="3" md="6" sm="12" className="mt-1">
              <Label className="mb-1">Status</Label>
              <div
                onChange={(e) =>
                  setData((p) => ({ ...p, status: e.target.value }))
                }
              >
                <label className="me-3">
                  <input
                    type="radio"
                    name="status"
                    value="Active"
                    defaultChecked={Data.status === "Active"}
                  />{" "}
                  Active
                </label>
                <label>
                  <input
                    type="radio"
                    name="status"
                    value="Deactive"
                    defaultChecked={Data.status === "Deactive"}
                  />{" "}
                  Deactive
                </label>
              </div>
            </Col>

            {errors.unitPolicy && (
              <Col xs="12" className="mt-1">
                <div className="erp-help" style={{ color: "red" }}>
                  {errors.unitPolicy}
                </div>
              </Col>
            )}
          </Row>

          {/* Units editor */}
          <div className="erp-box" style={{ marginBottom: 12 }}>
            <div
              className="erp-title"
              style={{ fontSize: 14, marginBottom: 8 }}
            >
              Units
            </div>
            {formValues.map((row, i) => (
              <Row key={`unit-${i}`} className="align-items-end">
                <Col lg="4" md="6" sm="12" className="mt-1">
                  <Label>Unit {i + 1}</Label>
                  <Input
                    type="select"
                    name="unit"
                    value={row.unit}
                    onChange={(e) => handleUnitRow(i, e)}
                    required
                  >
                    <option value="">-- Select --</option>
                    {units.map((u) => (
                      <option key={u?.unitId} value={u?.unitId}>
                        {u?.unitName}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col lg="4" md="6" sm="12" className="mt-1">
                  <Label>Quantity {i + 1}</Label>
                  <Input
                    type="number"
                    step="0.00001"
                    min="0"
                    name="qty"
                    value={row.qty}
                    onChange={(e) => handleUnitRow(i, e)}
                    placeholder="Enter Qty"
                    required
                  />
                </Col>
                <Col lg="2" md="3" sm="4" className="mt-1">
                  {i > 0 && (
                    <Button color="danger" onClick={() => removeUnitRow(i)}>
                      ❌
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
            <Button color="primary" className="mt-2" onClick={addUnitRow}>
              Add
            </Button>
          </div>

          {/* Costing + Target sections appear only if this is the last step */}
          {isLastStep && (
            <>
              {/* Costing */}
              <div className="erp-box" style={{ marginBottom: 12 }}>
                <div
                  className="erp-title"
                  style={{ fontSize: 14, marginBottom: 8 }}
                >
                  Costing
                </div>

                {Data.rawValues.map((row, i) => (
                  <Row key={`raw-${i}`} className="align-items-end">
                    <Col lg="3" md="6" sm="12" className="mt-1">
                      <Label>Raw Material</Label>
                      <Input
                        type="select"
                        value={row.rawMaterial}
                        onChange={(e) => onRawMaterial(i, e)}
                        required
                      >
                        <option value="">-- Select --</option>
                        {purchaseProductList.map((p) => (
                          <option key={p?._id} value={p?.Product_Title}>
                            {p?.Product_Title}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col lg="2" md="6" sm="12" className="mt-1">
                      <Label>Units</Label>
                      <Input readOnly value={row.unit || ""} />
                    </Col>
                    <Col lg="2" md="6" sm="12" className="mt-1">
                      <Label>Qty</Label>
                      <Input
                        type="number"
                        step="0.00001"
                        min="0"
                        value={row.qty}
                        onChange={(e) => onRawQty(i, e)}
                        required
                      />
                    </Col>
                    <Col lg="2" md="6" sm="12" className="mt-1">
                      <Label>Rate</Label>
                      <Input readOnly value={row.rate || ""} />
                    </Col>
                    <Col lg="2" md="6" sm="12" className="mt-1">
                      <Label>Price</Label>
                      <Input readOnly value={row.Price || ""} />
                    </Col>
                    <Col lg="1" md="2" sm="3" className="mt-1">
                      {i > 0 && (
                        <Button color="danger" onClick={() => removeRawRow(i)}>
                          ❌
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <Button color="primary" onClick={addRawRow}>
                    Add
                  </Button>
                  <div className="erp-total">
                    <div className="d-flex justify-content-between">
                      <span>Gross Total:</span>
                      <strong>Rs {money(rawTotal)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span>Expense %:</span>
                      <Input
                        type="number"
                        value={Data.expensePercent}
                        onChange={(e) =>
                          setData((p) => ({
                            ...p,
                            expensePercent: toNum(e.target.value, 0),
                          }))
                        }
                        style={{ width: 70, textAlign: "right" }}
                      />
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <span>Net Amount:</span>
                      <strong>
                        Rs{" "}
                        {money(
                          rawTotal * (1 + toNum(Data.expensePercent) / 100)
                        )}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Target Required */}
              <div className="erp-box" style={{ marginBottom: 12 }}>
                <div
                  className="erp-title"
                  style={{ fontSize: 14, marginBottom: 8 }}
                >
                  Target Required
                </div>

                {Data.targetValues.map((row, i) => (
                  <div
                    key={`t-${i}`}
                    style={{
                      borderBottom: "1px dashed #e5e7eb",
                      paddingBottom: 10,
                      marginBottom: 10,
                    }}
                  >
                    <Row className="align-items-end">
                      <Col lg="3" md="6" sm="12" className="mt-1">
                        <Label>Process Step</Label>
                        <Input
                          type="select"
                          value={row.step}
                          onChange={(e) => onTargetStep(i, e)}
                          required
                        >
                          <option value="">-- Select --</option>
                          {subStepslist.map((s) => (
                            <option key={s?._id} value={s?.step_Name}>
                              {s?.step_Name}
                            </option>
                          ))}
                        </Input>
                      </Col>

                      <Col lg="4" md="6" sm="12" className="mt-1">
                        <Label>Raw Material</Label>
                        <Input
                          type="select"
                          value={row.rawMaterial}
                          onChange={(e) => onTargetMaterial(i, e)}
                          required
                        >
                          <option value="">-- Select --</option>
                          {rawProducts.map((p) => (
                            <option key={p?._id} value={p?.Product_Title}>
                              {p?.Product_Title}
                            </option>
                          ))}
                        </Input>
                      </Col>

                      <Col lg="2" md="3" sm="4" className="mt-1">
                        {i > 0 && (
                          <Button
                            color="danger"
                            onClick={() => removeTargetRow(i)}
                          >
                            ❌
                          </Button>
                        )}
                      </Col>
                    </Row>

                    {Array.isArray(row.unit) && row.unit.length > 0 && (
                      <Row className="mt-1">
                        {row.unit.map((u, ui) => (
                          <Col
                            key={`u-${ui}`}
                            lg="2"
                            md="3"
                            sm="6"
                            className="mt-1"
                          >
                            <Label>{u?.unit}</Label>
                            <Input
                              type="number"
                              step="0.00001"
                              min="0"
                              value={u?.qty || ""}
                              onChange={(e) => onTargetUnitQty(i, ui, e)}
                            />
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                ))}

                <Button color="primary" onClick={addTargetRow}>
                  Add
                </Button>
              </div>
            </>
          )}

          <div className="d-flex justify-content-center">
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
}
